/**
 * Wallet Repository
 *
 * Handles wallet summary operations with atomic balance updates,
 * plus legacy payment methods management.
 *
 * Firestore structure:
 * - users/{uid}/wallet/summary (balance, totals)
 * - users/{uid}/wallet/earnings/{id}
 * - users/{uid}/wallet/expenses/{id}
 * - users/{uid}/wallet/usage/{id}
 * - users/{uid}/user_wallet/user_wallet (legacy payment methods)
 */

import {
  runTransaction,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
} from 'firebase/firestore';
import { Paths, ts, withBackoff, setDoc, getDoc, doc, db } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';
import type { Expense, ExpenseStatus } from '../types/expenses';
import type { Earning, EarningStatus } from '../types/earnings';
import {
  WalletSummary,
  WalletSummaryDoc,
  fromWalletSummaryDoc,
  createDefaultWalletSummary,
  toExpenseDoc,
  toEarningDoc,
  toUsageDoc,
  generateWalletItemId,
  Usage,
  UsageStatus,
  AIProvider,
} from '../types/walletFirestore';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// ============================================================================
// Atomic Transaction Input Types
// ============================================================================

export interface CreateExpenseInput {
  /** Summary description */
  summary: string;
  /** Member ID */
  memberId: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'completed') */
  status?: ExpenseStatus;
}

export interface CreateEarningInput {
  /** Summary description */
  summary: string;
  /** Member ID */
  memberId: string;
  /** Source of earning */
  source: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'completed') */
  status?: EarningStatus;
}

export interface CreateUsageInput {
  /** Summary description */
  summary: string;
  /** Member ID */
  memberId: string;
  /** Source page/feature */
  source: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID */
  modelId: string;
  /** Provider name */
  provider: AIProvider;
  /** Initial status (defaults to 'successful') */
  status?: UsageStatus;
}

// ============================================================================
// Legacy Wallet Migration
// ============================================================================

// Track which UIDs have already been checked to avoid repeated migration attempts
const _migrationAttempted = new Set<string>();

/**
 * One-time migration: when the new `wallet/summary` doc doesn't exist,
 * check the two legacy wallet paths for balance data and seed the new doc.
 *
 * Legacy paths checked:
 *   1. users/{uid}/user_wallet_page/user_wallet_page  → wallet_olv_balance
 *   2. users/{uid}/user_wallet/user_wallet             → wallet_olv_balance
 */
async function migrateLegacyWalletIfNeeded(uid: string): Promise<void> {
  if (_migrationAttempted.has(uid)) return;
  _migrationAttempted.add(uid);

  try {
    let legacyOlvBalance = 0;
    let legacySource = '';

    // Check user_wallet_page first (created during account init)
    const walletPageRef = Paths.userWalletPageDoc(uid);
    const walletPageSnap = await withBackoff(() => getDoc(walletPageRef));
    if (walletPageSnap.exists()) {
      const wpData = walletPageSnap.data() as any;
      const wpBalance = wpData?.wallet_olv_balance;
      if (typeof wpBalance === 'number' && wpBalance !== 0) {
        legacyOlvBalance = wpBalance;
        legacySource = 'user_wallet_page';
      }
    }

    // Check legacy user_wallet doc
    if (legacyOlvBalance === 0) {
      const walletRef = Paths.userWalletDoc(uid);
      const walletSnap = await withBackoff(() => getDoc(walletRef));
      if (walletSnap.exists()) {
        const wData = walletSnap.data() as any;
        const wBalance = wData?.wallet_olv_balance;
        if (typeof wBalance === 'number' && wBalance !== 0) {
          legacyOlvBalance = wBalance;
          legacySource = 'user_wallet';
        }
      }
    }

    // Seed the new wallet/summary doc (even if balance is 0, so the doc exists)
    // IMPORTANT: Check if it exists before overwriting, to avoid racing with welcome credit
    const summaryRef = Paths.walletSummaryDoc(uid);
    const existingSummary = await withBackoff(() => getDoc(summaryRef));
    if (!existingSummary.exists()) {
      await withBackoff(() =>
        setDoc(summaryRef, {
          olv_balance: legacyOlvBalance,
          total_olv_earned: 0,
          total_olv_spent: 0,
          last_updated: serverTimestamp(),
        })
      );

    }
  } catch (err) {
    console.error('[WalletRepository] Legacy wallet migration failed:', err);
    // Don't block — the listener will still return defaults
  }
}

// ============================================================================
// Wallet Summary Operations
// ============================================================================

/**
 * Get the current wallet summary
 */
export async function getWalletSummary(uid: string): Promise<WalletSummary> {
  const ref = Paths.walletSummaryDoc(uid);
  const snap = await withBackoff(() => getDoc(ref));

  if (!snap.exists()) {
    // Attempt to migrate from legacy paths — after migration the caller
    // should re-read (or the real-time listener will pick up the new doc).
    await migrateLegacyWalletIfNeeded(uid);
    const retrySnap = await withBackoff(() => getDoc(ref));
    if (retrySnap.exists()) {
      return fromWalletSummaryDoc(retrySnap.data() as WalletSummaryDoc);
    }
    return createDefaultWalletSummary();
  }

  return fromWalletSummaryDoc(snap.data() as WalletSummaryDoc);
}

/**
 * Subscribe to wallet summary with real-time updates
 */
export function onWalletSummarySnapshot(
  uid: string,
  onData: (summary: WalletSummary) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = Paths.walletSummaryDoc(uid);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        console.warn('[WalletRepository] wallet/summary NOT FOUND at:', ref.path);
        // Return defaults immediately so the UI isn't stuck loading
        onData(createDefaultWalletSummary());
        // Kick off migration in the background — once the new doc is written,
        // this same onSnapshot listener will fire again with real data.
        migrateLegacyWalletIfNeeded(uid);
        return;
      }
      const summary = fromWalletSummaryDoc(snap.data() as WalletSummaryDoc);
      onData(summary);
    },
    (error) => {
      console.error('[WalletRepository] Wallet summary snapshot error:', error);
      onError?.(error);
    }
  );
}

// ============================================================================
// Account wallet DISPLAY mirror (owner decision 2026-07-13: "display")
// ============================================================================

/**
 * Snapshot of accounts/{accountId}/wallet/displaySummary — the server-written
 * DISPLAY mirror of whichever wallet actually PAYS for the account's usage
 * (owner-personal or workspace pool; see functions/src/lib/accountExpense.ts).
 * Balance fields only; readable by verified account members (firestore.rules
 * accounts/{id}/wallet/** block); written exclusively by Cloud Functions.
 */
export interface AccountWalletDisplaySnapshot {
  /** Whether the mirror doc exists (false until the account's first debit). */
  exists: boolean;
  /** Converted balance summary (null when the doc doesn't exist). */
  summary: WalletSummary | null;
  /**
   * Whose PERSONAL wallet pays (owner-personal mode) or null for the shared
   * workspace pool. Lets the client prefer its own live wallet feed when the
   * signed-in user IS the payer.
   */
  payerUid: string | null;
  /** 'owner-personal' | 'workspace' as stamped by the server (null if absent). */
  walletMode: string | null;
}

/**
 * Subscribe to the account wallet display mirror with real-time updates.
 * onError fires for permission errors (e.g. not a verified member) — callers
 * must treat that as "fall back to the personal wallet".
 */
export function onAccountWalletDisplaySnapshot(
  accountId: string,
  onData: (snapshot: AccountWalletDisplaySnapshot) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = doc(db, 'accounts', accountId, 'wallet', 'displaySummary');
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onData({ exists: false, summary: null, payerUid: null, walletMode: null });
        return;
      }
      const data = snap.data() as WalletSummaryDoc & { payer_uid?: string | null; wallet_mode?: string };
      onData({
        exists: true,
        summary: fromWalletSummaryDoc(data),
        payerUid: typeof data.payer_uid === 'string' && data.payer_uid ? data.payer_uid : null,
        walletMode: typeof data.wallet_mode === 'string' && data.wallet_mode ? data.wallet_mode : null,
      });
    },
    (error) => {
      console.warn('[WalletRepository] account wallet display snapshot error:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to the acting-account back-pointer (users/{uid}/account_membership/main
 * → accountId) used to resolve WHICH account's paying wallet to display. Always
 * self-readable per firestore.rules; emits null when absent. Kept here (rather
 * than the prest0nRuntime resolver, which is one-shot/cached) because the wallet
 * display must react to membership changes in real time.
 */
export function onAccountMembershipAccountIdSnapshot(
  uid: string,
  onData: (accountId: string | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = doc(db, 'users', uid, 'account_membership', 'main');
  return onSnapshot(
    ref,
    (snap) => {
      // Full back-pointer chain (org-stickiness round 2, 2026-08-19): the
      // narrow accountId-only read showed the PERSONAL wallet to members
      // whose pointer still had the legacy invite-accept shape.
      const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
      onData(pickBackPointerAccountId(data));
    },
    (error) => {
      console.warn('[WalletRepository] account membership snapshot error:', error);
      onError?.(error);
    }
  );
}

// ============================================================================
// Atomic Transaction Operations
// ============================================================================

/**
 * Create an expense AND update wallet summary balance atomically
 * Deducts from balance, adds to total_olv_spent (tracks OLV at moment-in-time rate)
 */
export async function createExpenseWithBalanceUpdate(
  uid: string,
  input: CreateExpenseInput
): Promise<Expense> {
  addUserScopedWriteGuard(uid, 'createExpenseWithBalanceUpdate');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  // Check if this is a test item (should not affect balance)
  const isTestItem = tags.includes('test example');

  const expense: Expense = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    memberId: input.memberId,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'completed',
  };

  await runTransaction(db, async (transaction) => {
    // 1. Read current summary
    const summaryRef = Paths.walletSummaryDoc(uid);
    const summarySnap = await transaction.get(summaryRef);
    const currentData = summarySnap.exists()
      ? (summarySnap.data() as WalletSummaryDoc)
      : { olv_balance: 0, usd_balance: 0, total_olv_earned: 0, total_olv_spent: 0 };

    // 2. Calculate new balance (DEDUCT for expense)
    const newOlvBalance = (currentData.olv_balance || 0) - input.amountOlv;
    const newUsdBalance = (currentData.usd_balance || 0) - input.amountUsd;
    // Track OLV spent (at moment-in-time rate)
    const newTotalOlvSpent = (currentData.total_olv_spent || 0) + input.amountOlv;

    // 3. Write expense document
    const expenseRef = Paths.walletExpenseDoc(uid, id);
    transaction.set(expenseRef, toExpenseDoc(expense));

    // 4. Update summary with new balance ONLY if not a test item
    // NOTE: Only olv_balance is stored - usd_balance is derived at read time from olv_balance * rate
    if (!isTestItem) {
      transaction.set(summaryRef, {
        olv_balance: newOlvBalance,
        // usd_balance is NOT stored - it's derived from olv_balance * exchange_rate at read time
        total_olv_earned: currentData.total_olv_earned || 0,
        total_olv_spent: newTotalOlvSpent,
        last_updated: serverTimestamp(),
      });
    } else {

    }
  });

  return expense;
}

/**
 * Create an earning AND update wallet summary balance atomically
 * Adds to balance, adds to total_olv_earned (tracks OLV at moment-in-time rate)
 */
export async function createEarningWithBalanceUpdate(
  uid: string,
  input: CreateEarningInput
): Promise<Earning> {
  addUserScopedWriteGuard(uid, 'createEarningWithBalanceUpdate');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  // Check if this is a test item (should not affect balance)
  const isTestItem = tags.includes('test example');

  const earning: Earning = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    memberId: input.memberId,
    source: input.source,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'completed',
  };

  await runTransaction(db, async (transaction) => {
    // 1. Read current summary
    const summaryRef = Paths.walletSummaryDoc(uid);
    const summarySnap = await transaction.get(summaryRef);
    const currentData = summarySnap.exists()
      ? (summarySnap.data() as WalletSummaryDoc)
      : { olv_balance: 0, usd_balance: 0, total_olv_earned: 0, total_olv_spent: 0 };

    // 2. Calculate new balance (ADD for earning)
    const newOlvBalance = (currentData.olv_balance || 0) + input.amountOlv;
    const newUsdBalance = (currentData.usd_balance || 0) + input.amountUsd;
    // Track OLV earned (at moment-in-time rate)
    const newTotalOlvEarned = (currentData.total_olv_earned || 0) + input.amountOlv;

    // 3. Write earning document
    const earningRef = Paths.walletEarningDoc(uid, id);
    transaction.set(earningRef, toEarningDoc(earning));

    // 4. Update summary with new balance ONLY if not a test item
    // NOTE: Only olv_balance is stored - usd_balance is derived at read time from olv_balance * rate
    if (!isTestItem) {
      transaction.set(summaryRef, {
        olv_balance: newOlvBalance,
        // usd_balance is NOT stored - it's derived from olv_balance * exchange_rate at read time
        total_olv_earned: newTotalOlvEarned,
        total_olv_spent: currentData.total_olv_spent || 0,
        last_updated: serverTimestamp(),
      });
    } else {

    }
  });

  return earning;
}

/**
 * Create a usage record AND update wallet summary balance atomically
 * Deducts from balance (usage is a cost), adds to total_olv_spent (tracks OLV at moment-in-time rate)
 */
export async function createUsageWithBalanceUpdate(
  uid: string,
  input: CreateUsageInput
): Promise<Usage> {
  addUserScopedWriteGuard(uid, 'createUsageWithBalanceUpdate');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  // Check if this is a test item (should not affect balance)
  const isTestItem = tags.includes('test example');

  const usage: Usage = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    memberId: input.memberId,
    source: input.source,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'successful',
  };

  await runTransaction(db, async (transaction) => {
    // 1. Read current summary
    const summaryRef = Paths.walletSummaryDoc(uid);
    const summarySnap = await transaction.get(summaryRef);
    const currentData = summarySnap.exists()
      ? (summarySnap.data() as WalletSummaryDoc)
      : { olv_balance: 0, usd_balance: 0, total_olv_earned: 0, total_olv_spent: 0 };

    // 2. Calculate new balance (DEDUCT for usage - it's a cost)
    const newOlvBalance = (currentData.olv_balance || 0) - input.amountOlv;
    const newUsdBalance = (currentData.usd_balance || 0) - input.amountUsd;
    // Track OLV spent (at moment-in-time rate)
    const newTotalOlvSpent = (currentData.total_olv_spent || 0) + input.amountOlv;

    // 3. Write usage document
    const usageRef = Paths.walletUsageDoc(uid, id);
    transaction.set(usageRef, toUsageDoc(usage));

    // 4. Update summary with new balance ONLY if not a test item
    // NOTE: Only olv_balance is stored - usd_balance is derived at read time from olv_balance * rate
    if (!isTestItem) {
      transaction.set(summaryRef, {
        olv_balance: newOlvBalance,
        // usd_balance is NOT stored - it's derived from olv_balance * exchange_rate at read time
        total_olv_earned: currentData.total_olv_earned || 0,
        total_olv_spent: newTotalOlvSpent,
        last_updated: serverTimestamp(),
      });
    } else {

    }
  });

  return usage;
}

// Wallet Page document model stored at users/{uid}/user_wallet/user_wallet
export type UserWalletCard = {
  full_name_on_card?: string;
  card_type?: string;
  last_four_digits_of_card_number?: string;
  expiration_date?: string;
};

export type UserWalletBankAccount = {
  full_name_on_account?: string;
  account_type?: string;
  last_four_digits_of_account_number?: string;
  // Alias to match provided Firebase structure variant
  last_fourdigits_of_account_number?: string;
};

export type UserWalletEntry = {
  wallet_olv_balance?: number;
  wallet_usd_balance?: number;
  sequence_olv_limit?: number;
  payment_card?: UserWalletCard; // legacy compatibility (derived from saved_cards[0])
  bank_account?: UserWalletBankAccount; // legacy compatibility (derived from saved_bank_accounts[0])
};

// New canonical wallet doc model (v2)
export type UserWalletDoc = {
  user_wallet_lastupdated?: any;
  wallet_olv_balance?: number;
  wallet_usd_balance?: number;
  sequence_olv_limit?: number;
  stripe_customer_id?: string; // canonical
  saved_cards?: UserWalletCard[];
  saved_bank_accounts?: UserWalletBankAccount[];
  // Legacy field retained for backward compatibility
  wallet_page?: UserWalletEntry[];
};

export async function getUserWalletEntry(uid: string): Promise<UserWalletEntry | null> {
  const ref = Paths.userWalletDoc(uid);
  const { getDoc } = await import('./firestoreUtils');
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return null;
  const data = snap.data() as UserWalletDoc;
  const entry: UserWalletEntry = {
    wallet_olv_balance: data.wallet_olv_balance,
    wallet_usd_balance: data.wallet_usd_balance,
    sequence_olv_limit: data.sequence_olv_limit,
  };
  // Prefer new arrays
  const firstCard = Array.isArray(data.saved_cards) && data.saved_cards.length ? data.saved_cards[0] : undefined;
  const firstBank = Array.isArray(data.saved_bank_accounts) && data.saved_bank_accounts.length ? data.saved_bank_accounts[0] : undefined;
  if (firstCard) entry.payment_card = firstCard;
  if (firstBank) entry.bank_account = firstBank;
  // Fallback to legacy wallet_page[0]
  if (!firstCard || !firstBank) {
    const legacy = Array.isArray(data.wallet_page) && data.wallet_page.length ? data.wallet_page[0] : undefined;
    if (legacy?.payment_card && !firstCard) entry.payment_card = legacy.payment_card;
    if (legacy?.bank_account && !firstBank) entry.bank_account = legacy.bank_account as any;
  }
  return entry;
}

export async function saveUserWalletEntry(uid: string, entry: UserWalletEntry): Promise<void> {
  addUserScopedWriteGuard(uid, 'saveUserWalletEntry');
  const ref = Paths.userWalletDoc(uid);
  
  try {
    const snap = await withBackoff(() => getDoc(ref));
    const current = (snap.exists() ? (snap.data() as UserWalletDoc) : {}) || {};
    // Resolve numeric fields to concrete numbers to avoid undefined writes on first save
    const resolvedOlv =
      typeof entry.wallet_olv_balance === 'number'
        ? entry.wallet_olv_balance
        : typeof current.wallet_olv_balance === 'number'
        ? current.wallet_olv_balance
        : 0;
    const resolvedUsd =
      typeof entry.wallet_usd_balance === 'number'
        ? entry.wallet_usd_balance
        : typeof current.wallet_usd_balance === 'number'
        ? current.wallet_usd_balance
        : 0;
    const resolvedSeq =
      typeof entry.sequence_olv_limit === 'number'
        ? entry.sequence_olv_limit
        : typeof current.sequence_olv_limit === 'number'
        ? current.sequence_olv_limit
        : 0;
    const next: UserWalletDoc = {
      user_wallet_lastupdated: ts.serverNow(),
      wallet_olv_balance: resolvedOlv,
      wallet_usd_balance: resolvedUsd,
      sequence_olv_limit: resolvedSeq,
      saved_cards: Array.isArray(current.saved_cards) ? [...current.saved_cards] : [],
      saved_bank_accounts: Array.isArray(current.saved_bank_accounts) ? [...current.saved_bank_accounts] : []
    };

    // Upsert card by last4
    if (entry.payment_card) {
      const c = entry.payment_card;
      const last4 = c.last_four_digits_of_card_number || '';
      const idx = next.saved_cards!.findIndex(x => (x.last_four_digits_of_card_number || '') === last4 && last4);
      const record: UserWalletCard = {
        full_name_on_card: c.full_name_on_card || '',
        card_type: c.card_type || '',
        last_four_digits_of_card_number: last4,
        expiration_date: c.expiration_date || ''
      };
      if (idx >= 0) next.saved_cards![idx] = record; else next.saved_cards!.unshift(record);
    }

    // Upsert bank by last4
    if (entry.bank_account) {
      const b = entry.bank_account;
      const last4 = b.last_four_digits_of_account_number || b.last_fourdigits_of_account_number || '';
      const idx = next.saved_bank_accounts!.findIndex(x => (x.last_four_digits_of_account_number || '') === last4 && last4);
      const record: UserWalletBankAccount = {
        full_name_on_account: b.full_name_on_account || '',
        account_type: b.account_type || '',
        last_four_digits_of_account_number: last4
      };
      if (idx >= 0) next.saved_bank_accounts![idx] = record; else next.saved_bank_accounts!.unshift(record);
    }

    await withBackoff(() => setDoc(ref, next, { merge: true }));
  } catch (error) {
    console.error('❌ Failed to save wallet entry:', error);
    throw error;
  }
}


export async function removeSavedCard(uid: string, lastFourDigits: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'removeSavedCard');
  const ref = Paths.userWalletDoc(uid);
  const snap = await withBackoff(() => getDoc(ref));
  const current = (snap.exists() ? (snap.data() as UserWalletDoc) : {}) || {};
  const currentCards = Array.isArray(current.saved_cards) ? current.saved_cards : [];
  const filtered = currentCards.filter(c => (c.last_four_digits_of_card_number || '') !== lastFourDigits);
  const next: Partial<UserWalletDoc> = {
    user_wallet_lastupdated: ts.serverNow(),
    saved_cards: filtered
  };
  await withBackoff(() => setDoc(ref, next, { merge: true }));
}

export async function removeSavedBankAccount(uid: string, lastFourDigits: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'removeSavedBankAccount');
  const ref = Paths.userWalletDoc(uid);
  const snap = await withBackoff(() => getDoc(ref));
  const current = (snap.exists() ? (snap.data() as UserWalletDoc) : {}) || {};
  const currentBanks = Array.isArray(current.saved_bank_accounts) ? current.saved_bank_accounts : [];
  const filtered = currentBanks.filter(b => (b.last_four_digits_of_account_number || b.last_fourdigits_of_account_number || '') !== lastFourDigits);
  const next: Partial<UserWalletDoc> = {
    user_wallet_lastupdated: ts.serverNow(),
    saved_bank_accounts: filtered
  };
  await withBackoff(() => setDoc(ref, next, { merge: true }));
}

// ============================================================================
// New User Wallet Initialization ($100 Welcome Credit)
// ============================================================================

const WELCOME_CREDIT_USD = 100;
const FALLBACK_OLV_RATE = 0.001;
const WELCOME_CREDIT_EARNING_ID = 'welcome-credit';

/**
 * Fetch the current OLV rate from Firestore.
 * Falls back to FALLBACK_OLV_RATE if unavailable.
 */
async function fetchOlvRate(): Promise<number> {
  try {
    const rateRef = doc(db, 'global_olv_token', 'token_price');
    const snap = await withBackoff(() => getDoc(rateRef));
    if (snap.exists()) {
      const data = snap.data() as any;
      const raw = data.token_price !== undefined ? data.token_price : data.price;
      const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
      if (!isNaN(parsed) && isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch (e) {
    console.warn('[WalletRepository] fetchOlvRate failed, using fallback:', e);
  }
  return FALLBACK_OLV_RATE;
}

/**
 * Initialize a new user's wallet with a $100 welcome credit.
 * Idempotent — only runs if users/{uid}/wallet/summary does not yet exist.
 *
 * Creates:
 * - wallet/summary doc with the OLV equivalent of $100
 * - a wallet/summary/earnings/{id} doc recording the welcome credit
 */
export async function initializeNewUserWallet(uid: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'initializeNewUserWallet');

  const summaryRef = Paths.walletSummaryDoc(uid);
  const earningRef = Paths.walletEarningDoc(uid, WELCOME_CREDIT_EARNING_ID);

  const olvRate = await fetchOlvRate();
  const amountOlv = WELCOME_CREDIT_USD / olvRate;
  const now = Date.now();

  await runTransaction(db, async (transaction) => {
    const [summarySnap, earningSnap] = await Promise.all([
      transaction.get(summaryRef),
      transaction.get(earningRef),
    ]);

    const summaryData = summarySnap.exists() ? (summarySnap.data() as any) : null;
    const existingOlv = typeof summaryData?.olv_balance === 'number' ? summaryData.olv_balance : 0;
    const existingEarned = typeof summaryData?.total_olv_earned === 'number' ? summaryData.total_olv_earned : 0;
    const existingSpent = typeof summaryData?.total_olv_spent === 'number' ? summaryData.total_olv_spent : 0;

    // 2026-08-02 (adversarial review): skip the welcome credit only when the
    // wallet already holds REAL money (e.g. the server-side trial/credit seed
    // from an access-code redemption). A bare summary doc with zero balance
    // does NOT count — the wallet-summary listener's legacy-migration path
    // seeds an empty doc mid-signup, and treating that as "exists" was
    // silently stranding brand-new organic users at $0.
    if (existingOlv > 0 || existingEarned > 0) {
      return;
    }

    // Idempotent: if the welcome-credit earning already exists, don't add it again.
    if (earningSnap.exists()) {
      if (!summarySnap.exists()) {
        transaction.set(summaryRef, {
          olv_balance: existingOlv,
          total_olv_earned: existingEarned,
          total_olv_spent: existingSpent,
          last_updated: serverTimestamp(),
        }, { merge: true });
      }
      return;
    }

    transaction.set(summaryRef, {
      olv_balance: existingOlv + amountOlv,
      total_olv_earned: existingEarned + amountOlv,
      total_olv_spent: existingSpent,
      last_updated: serverTimestamp(),
    }, { merge: true });

    transaction.set(earningRef, {
      id: WELCOME_CREDIT_EARNING_ID,
      timestamp: ts.fromMillis(now),
      tags: ['welcome', 'credit'],
      summary: 'welcome credit',
      source: 'signup',
      member_id: uid,
      amount_usd: WELCOME_CREDIT_USD,
      amount_olv: amountOlv,
      olv_rate_at_time: olvRate,
      status: 'completed',
      created_at: serverTimestamp(),
    });
  });


}


